import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, X } from 'lucide-react';
import { getAnchoredOverlayStyle, isRectOutsideViewport } from '../../utils/viewportPosition';

const DateRangePicker = ({
  label,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  // New simplified API
  startDate,
  endDate,
  onChange,
  placeholder = "Select date range",
  className = ""
}) => {
  // Support both old API (fromValue/toValue/onFromChange/onToChange) and new API (startDate/endDate/onChange)
  const actualFromValue = startDate !== undefined ? startDate : fromValue;
  const actualToValue = endDate !== undefined ? endDate : toValue;
  const actualOnFromChange = onChange ? (value) => onChange({ startDate: value, endDate: actualToValue }) : onFromChange;
  const actualOnToChange = onChange ? (value) => onChange({ startDate: actualFromValue, endDate: value }) : onToChange;

  const [isOpen, setIsOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState(actualFromValue || '');
  const [tempToDate, setTempToDate] = useState(actualToValue || '');
  const [hoveredDate, setHoveredDate] = useState(null);
  const [calendarStyle, setCalendarStyle] = useState({});
  const containerRef = useRef(null);
  const calendarRef = useRef(null);

  const updateCalendarPosition = useCallback(() => {
    if (!containerRef.current || !isOpen) return;
    const rect = containerRef.current.getBoundingClientRect();
    const viewport = {
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };

    if (isRectOutsideViewport(rect, viewport)) {
      setIsOpen(false);
      return;
    }

    setCalendarStyle(getAnchoredOverlayStyle({
      anchorRect: rect,
      ...viewport,
      preferredWidth: Math.max(320, rect.width),
      preferredHeight: 460,
    }));
  }, [isOpen]);

  // Закрытие календаря при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current
        && !containerRef.current.contains(event.target)
        && !calendarRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    if (!isOpen) return undefined;

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateCalendarPosition();
    window.addEventListener('resize', updateCalendarPosition);
    window.addEventListener('scroll', updateCalendarPosition, true);
    return () => {
      window.removeEventListener('resize', updateCalendarPosition);
      window.removeEventListener('scroll', updateCalendarPosition, true);
    };
  }, [isOpen, updateCalendarPosition]);

  // Обновление временных значений при изменении пропсов
  useEffect(() => {
    setTempFromDate(actualFromValue || '');
    setTempToDate(actualToValue || '');
  }, [actualFromValue, actualToValue]);

  const formatDisplayValue = () => {
    if (actualFromValue && actualToValue) {
      return `${formatDate(actualFromValue)} - ${formatDate(actualToValue)}`;
    } else if (actualFromValue) {
      return `From ${formatDate(actualFromValue)}`;
    } else if (actualToValue) {
      return `To ${formatDate(actualToValue)}`;
    }
    return placeholder;
  };

  const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const clearDates = () => {
    setTempFromDate('');
    setTempToDate('');
    if (onChange) {
      onChange({ startDate: '', endDate: '' });
    } else {
      if (actualOnFromChange) actualOnFromChange('');
      if (actualOnToChange) actualOnToChange('');
    }
    setIsOpen(false);
  };

  const applyDates = () => {
    if (onChange) {
      onChange({ startDate: tempFromDate, endDate: tempToDate });
    } else {
      if (actualOnFromChange) actualOnFromChange(tempFromDate);
      if (actualOnToChange) actualOnToChange(tempToDate);
    }
    setIsOpen(false);
  };

  const generateCalendarDays = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Получаем день недели первого дня месяца (0 = воскресенье, 1 = понедельник, ...)
    const firstDayOfWeek = firstDay.getDay();
    
    // Конвертируем в российский формат (0 = понедельник, 6 = воскресенье)
    const russianFirstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Начинаем календарь с понедельника предыдущей недели
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - russianFirstDayOfWeek);
    
    const days = [];
    const currentDate = new Date(startDate);
    
    // Генерируем 42 дня (6 недель) для полного отображения календаря
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  const isDateInRange = (date, fromDate, toDate) => {
    if (!fromDate || !toDate) return false;
    const dateStr = getDateString(date);
    return dateStr >= fromDate && dateStr <= toDate;
  };

  const isDateSelected = (date, selectedDate) => {
    if (!selectedDate) return false;
    return getDateString(date) === selectedDate;
  };

  const handleDateClick = (date) => {
    const dateStr = getDateString(date);
    
    if (!tempFromDate || (tempFromDate && tempToDate)) {
      // Начинаем новый выбор
      setTempFromDate(dateStr);
      setTempToDate('');
    } else if (tempFromDate && !tempToDate) {
      // Завершаем выбор
      if (dateStr >= tempFromDate) {
        setTempToDate(dateStr);
      } else {
        setTempToDate(tempFromDate);
        setTempFromDate(dateStr);
      }
    }
  };

  const handleDateHover = (date) => {
    if (tempFromDate && !tempToDate) {
      setHoveredDate(getDateString(date));
    }
  };

  const handleDateLeave = () => {
    setHoveredDate(null);
  };

  const getCurrentMonth = () => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  };

  const [currentMonth, setCurrentMonth] = useState(getCurrentMonth());

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev.year, prev.month + direction, 1);
      return { year: newDate.getFullYear(), month: newDate.getMonth() };
    });
  };

  const days = generateCalendarDays(currentMonth.year, currentMonth.month);
  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  return (
    <div className={`relative w-full min-w-0 ${className}`} ref={containerRef}>
      <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={formatDisplayValue()}
          readOnly
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 pr-20 border border-light-border-primary dark:border-dark-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary"
          placeholder={placeholder}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <Calendar className="h-4 w-4 text-light-text-tertiary dark:text-dark-text-tertiary" />
        </div>
      </div>

      {isOpen && createPortal(
        <div
          ref={calendarRef}
          style={calendarStyle}
          className="min-w-0 overflow-y-auto rounded-lg border border-light-border-primary bg-light-bg-primary p-3 shadow-lg dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-4"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {monthNames[currentMonth.month]} {currentMonth.year}
            </h3>
            <div className="flex gap-1">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-1 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary rounded text-light-text-primary dark:text-dark-text-primary"
                title="Предыдущий месяц"
              >
                ←
              </button>
              <button
                onClick={() => navigateMonth(1)}
                className="p-1 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary rounded text-light-text-primary dark:text-dark-text-primary"
                title="Следующий месяц"
              >
                →
              </button>
            </div>
          </div>

          {/* Calendar */}
          <div className="mb-4">
            {/* Days of week */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
                <div key={day} className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary text-center py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => {
                const dateStr = getDateString(date);
                const isCurrentMonth = date.getMonth() === currentMonth.month;
                const isCurrentYear = date.getFullYear() === currentMonth.year;
                const isToday = dateStr === getDateString(new Date());
                const hoverMin = tempFromDate < hoveredDate ? tempFromDate : hoveredDate;
                const hoverMax = tempFromDate > hoveredDate ? tempFromDate : hoveredDate;
                const isInRange = isDateInRange(date, tempFromDate, tempToDate) ||
                                 (tempFromDate && hoveredDate &&
                                  dateStr >= hoverMin &&
                                  dateStr <= hoverMax);
                const isFromSelected = isDateSelected(date, tempFromDate);
                const isToSelected = isDateSelected(date, tempToDate);

                return (
                  <button
                    key={index}
                    onClick={() => handleDateClick(date)}
                    onMouseEnter={() => handleDateHover(date)}
                    onMouseLeave={handleDateLeave}
                    className={`
                      h-8 w-8 text-xs rounded-full flex items-center justify-center
                      ${!isCurrentMonth || !isCurrentYear ? 'text-light-text-tertiary dark:text-dark-text-tertiary' : 'text-light-text-primary dark:text-dark-text-primary hover:bg-blue-100 dark:hover:bg-blue-900/30'}
                      ${isToday ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                      ${isFromSelected || isToSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                      ${isInRange && !isFromSelected && !isToSelected ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
                    `}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={clearDates}
              className="flex items-center space-x-1 text-sm text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              <X className="h-4 w-4" />
              <span>Очистить</span>
            </button>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setIsOpen(false)}
                className="px-3 py-1 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              >
                Отмена
              </button>
              <button
                onClick={applyDates}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Применить
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DateRangePicker;
